import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  List,
  ListItemButton,
  ListItemText,
  IconButton,
  Menu,
  MenuItem,
} from "@mui/material";
import DivisionDetails from "./division-details/division-details";
import AddDivision from "./add-division/add-division";
import DivisionDelete from "./division-delete/division-delete";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { useDispatch, useSelector } from "react-redux";
import {
  getClientSubDivisionListRequest,
  getClientSubDivisionListSuccess,
  getSubdivisionDetailsRequest,
  getSubdivisionDetailsSuccess,
} from "../../../../../redux/clients/slice";
import Loading from "../../../../../components/loading/loading";
import ErrorNotification from "../../../../../components/error-notification/error-notification";
import Notification from "../../../../../components/notification/notification";
import "./client-divisions.css";

const options = ["Delete"];
const ITEM_HEIGHT = 48;

export default function ClientDivisions({ data }: any) {
  const [subDivisions, setSubDivisions] = useState<any[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [hoveredIndex, setHoveredIndex] = useState(-1);
  const [showAddDivision, setShowAddDivision] = useState(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [showListItemMenu, setShowListItemMenu] = useState(false);
  const [addedDivision, setAddedDivision] = useState<any>();
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [showDivisionDelete, setShowDivisionDelete] = useState(false);
  const [divisionToDelete, setDivisionToDelete] = useState<{
    id: string;
    index: number;
  } | null>(null);

  const dispatch = useDispatch();

  const { subDivisionList, loading } = useSelector(
    (state: any) => state?.clients
  );
  const { subDivisionDetails } = useSelector((state: any) => state?.clients);

  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };
  const handleClose = (event: any) => {
    event.stopPropagation();
    setAnchorEl(null);
    setShowListItemMenu(false);
  };

  useEffect(() => {
    if (data) {
      dispatch(getSubdivisionDetailsSuccess({ response: null }));
      dispatch(getClientSubDivisionListSuccess({ response: null }));
      dispatch(getClientSubDivisionListRequest({ id: data?.id }));
    }
  }, [dispatch, data]);

  useEffect(() => {
    if (subDivisionList) {
      setSubDivisions(subDivisionList);
    }
  }, [subDivisionList]);

  useEffect(() => {
    if (subDivisions && subDivisions?.length > 0) {
      if (addedDivision) {
        const index = subDivisions.findIndex(
          (item: any) => item.id === addedDivision?.id
        );
        setSelectedIndex(index);
        dispatch(
          getSubdivisionDetailsRequest({
            id: data?.id,
            subDivisionId: addedDivision?.id,
          })
        );
      } else {
        setSelectedIndex(0);
        dispatch(
          getSubdivisionDetailsRequest({
            id: data?.id,
            subDivisionId: subDivisions[0]?.id,
          })
        );
      }
    }
  }, [subDivisions, addedDivision]); // eslint-disable-line

  const handleListItemClick = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
    index: number,
    item: any
  ) => {
    setSelectedIndex(index);
    dispatch(
      getSubdivisionDetailsRequest({ id: data?.id, subDivisionId: item?.id })
    );
  };

  const handleListItemHover = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
    index: number
  ) => {
    setHoveredIndex(index);
    setShowListItemMenu(true);
    setAnchorEl(null);
  };

  const onAddDivision = (data: any) => {
    setAddedDivision(data);
  };

  const onDeleteDivision = (index: number) => {
    setDivisionToDelete({
      id: subDivisions[index]?.id,
      index,
    });
    setShowDivisionDelete(true);
    setAnchorEl(null);
    setShowListItemMenu(false);
  };

  const handleDeleteSuccess = () => {
    setShowSuccessNotification(true);
    dispatch(getClientSubDivisionListRequest({ id: data?.id }));
    setDivisionToDelete(null);
  };

  const handleDeleteError = (message: string) => {
    setShowErrorNotification(true);
    setErrorMessage(message);
  };

  return (
    <Box className="client-division-container">
      <Box className="client-division-list-layout">
        <List component="nav" aria-label="sub division" sx={{ width: 250 }}>
          {subDivisions?.map((item: any, index: number) => (
            <ListItemButton
              key={item.id}
              selected={selectedIndex === index}
              onClick={(event) => handleListItemClick(event, index, item)}
              onMouseEnter={(event) => handleListItemHover(event, index)}
              onMouseLeave={() => setShowListItemMenu(false)}
            >
              <ListItemText primary={item.displayName} />
              {showListItemMenu && hoveredIndex === index && (
                <Box>
                  <IconButton
                    aria-label="more"
                    id="long-button"
                    aria-controls={open ? "long-menu" : undefined}
                    aria-expanded={open ? "true" : undefined}
                    aria-haspopup="true"
                    onClick={handleClick}
                  >
                    <MoreHorizIcon />
                  </IconButton>
                  <Menu
                    id="long-menu"
                    MenuListProps={{
                      "aria-labelledby": "long-button",
                    }}
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleClose}
                    slotProps={{
                      paper: {
                        style: {
                          maxHeight: ITEM_HEIGHT * 4.5,
                          width: "20ch",
                        },
                      },
                    }}
                  >
                    {options.map((option) => (
                      <MenuItem
                        key={option}
                        onClick={() => onDeleteDivision(index)}
                      >
                        {option}
                      </MenuItem>
                    ))}
                  </Menu>
                </Box>
              )}
            </ListItemButton>
          ))}
        </List>
        <Button
          variant="text"
          startIcon={<AddOutlinedIcon />}
          onClick={() => setShowAddDivision(true)}
        >
          Add Sub Division
        </Button>
      </Box>

      {subDivisionDetails && (
        <Box className="client-division-details-layout">
          <DivisionDetails
            data={subDivisionDetails}
            clientId={data?.id}
            onAddDivision={onAddDivision}
          />
        </Box>
      )}

      {showAddDivision && (
        <AddDivision
          showDialog={showAddDivision}
          closeDialog={() => setShowAddDivision(false)}
          clientId={data?.id}
          onAddDivision={onAddDivision}
        />
      )}

      {showDivisionDelete && divisionToDelete && (
        <DivisionDelete
          showDialog={showDivisionDelete}
          closeDialog={() => {
            setShowDivisionDelete(false);
            setDivisionToDelete(null);
          }}
          id={divisionToDelete.id}
          clientId={data?.id}
          onSuccess={handleDeleteSuccess}
          onError={handleDeleteError}
        />
      )}

      {loading && <Loading />}
      <ErrorNotification
        show={showErrorNotification}
        message={errorMessage}
        onClose={() => setShowErrorNotification(false)}
      />
      <Notification
        show={showSuccessNotification}
        message="Division deleted successfully"
        onClose={() => setShowSuccessNotification(false)}
      />
    </Box>
  );
}
